import React, { useState, useMemo } from 'react';
import { Project } from '../../types/project';
import { Task } from '../../types/task';
import { useTasks } from '../../hooks/useTasks';
import { PlusIcon, ClockIcon, CalendarIcon, ChatBubbleLeftIcon, PencilIcon, TrashIcon, CheckCircleIcon, UserGroupIcon } from '@heroicons/react/24/outline';
import TaskView from '../tasks/TaskView';
import TaskForm from '../tasks/TaskForm';
import ConfirmDialog from '../common/ConfirmDialog';

interface ProjectBoardViewProps {
  project: Project;
}

interface BoardColumn {
  id: string;
  title: string;
  status: string;
  tasks: Task[];
  color: string;
}

const ProjectBoardView: React.FC<ProjectBoardViewProps> = ({ project }) => {
  const { tasks, loading, updateTask, deleteTask } = useTasks();
  const [draggedTask, setDraggedTask] = useState<Task | null>(null);
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [editingTask, setEditingTask] = useState<Task | null>(null);
  const [deletingTask, setDeletingTask] = useState<Task | null>(null);
  const [creatingTaskStatus, setCreatingTaskStatus] = useState<string | null>(null);

  // Projekt feladatainak szűrése
  const projectTasks = useMemo(() => {
    return tasks.filter(task => task.projectId === project.id);
  }, [tasks, project.id]);

  // Board oszlopok (Task státuszok)
  const columns: BoardColumn[] = useMemo(() => {
    const statuses = [
      { id: 'todo', title: 'To Do', status: 'todo', color: '#6B7280' },
      { id: 'in_progress', title: 'In Progress', status: 'in_progress', color: '#3B82F6' },
      { id: 'completed', title: 'Completed', status: 'completed', color: '#10B981' },
      { id: 'cancelled', title: 'Cancelled', status: 'cancelled', color: '#EF4444' }
    ];

    return statuses.map(status => ({
      ...status,
      tasks: projectTasks.filter(task => task.status === status.status)
    }));
  }, [projectTasks]);

  // Drag & Drop handlers
  const handleDragStart = (task: Task) => {
    setDraggedTask(task);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleDrop = async (columnStatus: string) => {
    if (!draggedTask) return;

    try {
      await updateTask(draggedTask.id, { status: columnStatus as any });
      setDraggedTask(null);
    } catch (error) {
      console.error('Error updating task status:', error);
    }
  };

  const handleDragEnd = () => {
    setDraggedTask(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-gray-500 dark:text-gray-400">Betöltés...</div>
      </div>
    );
  }

  // Haladás számítás
  const completedTasks = projectTasks.filter(t => t.status === 'completed').length;
  const progress = projectTasks.length > 0 ? Math.round((completedTasks / projectTasks.length) * 100) : 0;

  // Státusz szín
  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      planning: 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400',
      active: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400',
      completed: 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300',
      paused: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400',
      cancelled: 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400'
    };
    return colors[status] || 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300';
  };

  const getStatusLabel = (status: string) => {
    const labels: Record<string, string> = {
      planning: 'Tervezés',
      active: 'Aktív',
      completed: 'Befejezett',
      paused: 'Szünetel',
      cancelled: 'Törölve'
    };
    return labels[status] || status;
  };

  return (
    <div className="h-full">
      {/* Board Header - Modern Glassmorphism */}
      <div className="mb-6 bg-white/80 dark:bg-gray-800/80 backdrop-blur-xl rounded-2xl shadow-xl border border-gray-200/50 dark:border-gray-700/50 p-6">
        <div className="flex items-center justify-between flex-wrap gap-6">
          {/* Bal oldal - Projekt info */}
          <div className="flex items-center gap-5 flex-wrap">
            {/* Projekt ikon */}
            {project.icon && (
              <div 
                className="w-16 h-16 rounded-2xl flex items-center justify-center shadow-2xl text-3xl flex-shrink-0 ring-4 ring-white/50 dark:ring-gray-700/50"
                style={{ 
                  background: project.color 
                    ? `linear-gradient(135deg, ${project.color} 0%, ${project.color}dd 100%)`
                    : 'linear-gradient(135deg, #9333EA 0%, #7E22CE 100%)'
                }}
              >
                {project.icon}
              </div>
            )}
            
            {/* Projekt név és kulcs */}
            <div>
              <h3 className="text-2xl font-bold text-gray-900 dark:text-gray-100 flex items-center gap-3 mb-2">
                <span className="bg-gradient-to-r from-purple-600 to-purple-800 dark:from-purple-400 dark:to-purple-600 bg-clip-text text-transparent">
                  {project.name}
                </span>
                <span className="text-sm font-mono text-gray-500 dark:text-gray-400 bg-gradient-to-r from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-600 px-3 py-1 rounded-lg shadow-sm">
                  {project.key}
                </span>
              </h3>
              <div className="flex items-center gap-4 flex-wrap">
                {/* Státusz */}
                <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-bold shadow-sm ${getStatusColor(project.status)}`}>
                  {getStatusLabel(project.status)}
                </span>
                
                {/* Task számláló */}
                <span className="text-sm text-gray-700 dark:text-gray-300 flex items-center gap-2 bg-white/60 dark:bg-gray-700/60 px-3 py-1.5 rounded-lg backdrop-blur-sm">
                  <CheckCircleIcon className="h-4 w-4 text-purple-600 dark:text-purple-400" />
                  <span className="font-semibold">{completedTasks}</span>
                  <span className="text-gray-400 dark:text-gray-500">/</span>
                  <span>{projectTasks.length}</span>
                  <span className="text-gray-500 dark:text-gray-400">feladat</span>
                </span>
                
                {/* Haladás */}
                <div className="flex items-center gap-2 bg-white/60 dark:bg-gray-700/60 px-3 py-1.5 rounded-lg backdrop-blur-sm">
                  <div className="w-24 bg-gray-200 dark:bg-gray-600 rounded-full h-2 overflow-hidden">
                    <div
                      className="bg-gradient-to-r from-purple-500 via-purple-600 to-purple-700 h-2 rounded-full transition-all duration-500 shadow-lg shadow-purple-500/50"
                      style={{ width: `${progress}%` }}
                    />
                  </div>
                  <span className="text-sm font-bold text-purple-600 dark:text-purple-400 min-w-[3rem] text-right">{progress}%</span>
                </div>
                
                {/* Csapat */}
                {project.assignedUsers && project.assignedUsers.length > 0 && (
                  <span className="text-sm text-gray-700 dark:text-gray-300 flex items-center gap-2 bg-white/60 dark:bg-gray-700/60 px-3 py-1.5 rounded-lg backdrop-blur-sm">
                    <UserGroupIcon className="h-4 w-4 text-purple-600 dark:text-purple-400" />
                    <span className="font-semibold">{project.assignedUsers.length}</span>
                    <span className="text-gray-500 dark:text-gray-400">tag</span>
                  </span>
                )}
                
                {/* Dátumok */}
                {project.endDate && (
                  <span className="text-sm text-gray-700 dark:text-gray-300 flex items-center gap-2 bg-white/60 dark:bg-gray-700/60 px-3 py-1.5 rounded-lg backdrop-blur-sm">
                    <CalendarIcon className="h-4 w-4 text-purple-600 dark:text-purple-400" />
                    <span className="font-semibold">{new Date(project.endDate).toLocaleDateString('hu-HU', { month: 'short', day: 'numeric' })}</span>
                  </span>
                )}
              </div>
            </div>
          </div>
          
          {/* Jobb oldal - Akciók */}
          <button 
            onClick={() => setCreatingTaskStatus('todo')}
            className="group relative px-6 py-3 bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700 text-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 text-sm font-semibold flex items-center gap-2 overflow-hidden"
          >
            <div className="absolute inset-0 bg-white/20 translate-y-full group-hover:translate-y-0 transition-transform duration-300" />
            <PlusIcon className="h-5 w-5 relative z-10" />
            <span className="relative z-10">Új feladat</span>
          </button>
        </div>
      </div>

      {/* Kanban Board */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {columns.map((column, index) => (
          <div
            key={column.id}
            className={`bg-gray-50/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-xl p-4 min-h-[600px] border-2 shadow-lg ${
              index === 0 ? 'border-gray-300/50 dark:border-gray-700/50' :
              index === 1 ? 'border-blue-300/50 dark:border-blue-700/50' :
              index === 2 ? 'border-green-300/50 dark:border-green-700/50' :
              'border-red-300/50 dark:border-red-700/50'
            }`}
            onDragOver={handleDragOver}
            onDrop={() => handleDrop(column.status)}
          >
            {/* Column Header */}
            <div className="mb-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div
                    className="w-3 h-3 rounded-full shadow-sm"
                    style={{ backgroundColor: column.color }}
                  />
                  <h4 className="font-semibold text-gray-900 dark:text-gray-100">{column.title}</h4>
                </div>
                <span className="bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 px-2.5 py-1 rounded-full text-xs font-medium shadow-sm">
                  {column.tasks.length}
                </span>
              </div>
              <div className="h-1.5 rounded-full shadow-sm" style={{ backgroundColor: column.color }} />
            </div>

            {/* Tasks */}
            <div className="space-y-3">
              {column.tasks.length === 0 ? (
                <div className="text-center py-8 text-gray-400 dark:text-gray-600 text-sm">
                  Nincs feladat
                </div>
              ) : (
                column.tasks.map(task => (
                  <div
                    key={task.id}
                    draggable
                    onDragStart={() => handleDragStart(task)}
                    onDragEnd={handleDragEnd}
                    onClick={(e) => {
                      // Only open modal if not dragging
                      if (e.currentTarget === e.target || e.currentTarget.contains(e.target as Node)) {
                        setSelectedTask(task);
                      }
                    }}
                    className={`bg-white/90 dark:bg-gray-900/90 backdrop-blur-md rounded-xl p-4 shadow-md border-l-4 border-r border-t border-b border-gray-200/50 dark:border-gray-700/50 cursor-pointer hover:shadow-xl hover:scale-[1.02] hover:bg-white dark:hover:bg-gray-900 transition-all ${
                      draggedTask?.id === task.id ? 'opacity-50' : ''
                    } ${
                      task.priority === 'urgent' ? 'border-l-red-500' :
                      task.priority === 'high' ? 'border-l-orange-500' :
                      task.priority === 'medium' ? 'border-l-yellow-500' :
                      'border-l-green-500'
                    }`}
                  >
                    {/* Task Header */}
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1 min-w-0">
                        <h5 className="font-semibold text-gray-900 dark:text-gray-100 text-sm line-clamp-2 mb-1">
                          {task.title}
                        </h5>
                        {task.taskNumber && (
                          <span className="text-xs text-gray-500 dark:text-gray-400 font-mono">
                            #{task.taskNumber}
                          </span>
                        )}
                      </div>
                      <div className="flex items-start gap-1 ml-2 flex-shrink-0">
                        {/* Edit Button */}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setEditingTask(task);
                          }}
                          className="p-1.5 text-gray-400 hover:text-purple-600 dark:hover:text-purple-400 hover:bg-purple-50 dark:hover:bg-purple-900/20 rounded-md transition-colors"
                          title="Szerkesztés"
                        >
                          <PencilIcon className="h-4 w-4" />
                        </button>
                        {/* Delete Button */}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setDeletingTask(task);
                          }}
                          className="p-1.5 text-gray-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-md transition-colors"
                          title="Törlés"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </div>

                    {/* Priority Badge */}
                    {task.priority && (
                      <div className="mb-3">
                        <span
                          className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${
                            task.priority === 'urgent'
                              ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400'
                              : task.priority === 'high'
                              ? 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400'
                              : task.priority === 'medium'
                              ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400'
                              : 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                          }`}
                        >
                          {task.priority === 'urgent' ? 'Sürgős' :
                           task.priority === 'high' ? 'Magas' :
                           task.priority === 'medium' ? 'Közepes' : 'Alacsony'}
                        </span>
                      </div>
                    )}

                    {/* Task Description */}
                    {task.description && (
                      <p className="text-xs text-gray-600 dark:text-gray-400 mb-3 line-clamp-2">
                        {task.description}
                      </p>
                    )}

                    {/* Task Meta Info */}
                    <div className="space-y-2 mb-3">
                      {/* Due Date */}
                      {task.dueDate && (
                        <div className="flex items-center gap-2 text-xs">
                          <CalendarIcon className="h-3.5 w-3.5 text-purple-500" />
                          <span className={`${
                            new Date(task.dueDate) < new Date() 
                              ? 'text-red-600 dark:text-red-400 font-semibold' 
                              : 'text-gray-600 dark:text-gray-400'
                          }`}>
                            {new Date(task.dueDate).toLocaleDateString('hu-HU', { month: 'short', day: 'numeric' })}
                            {new Date(task.dueDate) < new Date() && ' (Lejárt)'}
                          </span>
                        </div>
                      )}
                      
                      {/* Estimated Time */}
                      {task.estimatedHours && (
                        <div className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-400">
                          <ClockIcon className="h-3.5 w-3.5 text-purple-500" />
                          <span>{task.estimatedHours}h becsült</span>
                        </div>
                      )}
                    </div>

                    {/* Task Footer */}
                    <div className="flex items-center justify-between pt-3 border-t border-gray-100 dark:border-gray-800">
                      <div className="flex items-center gap-2">
                        {task.assignedToName && (
                          <div className="flex items-center gap-2">
                            <div className="w-7 h-7 rounded-full bg-gradient-to-br from-purple-500 to-purple-600 flex items-center justify-center shadow-sm">
                              <span className="text-xs font-semibold text-white">
                                {task.assignedToName.charAt(0).toUpperCase()}
                              </span>
                            </div>
                            <span className="text-xs text-gray-600 dark:text-gray-400 font-medium">
                              {task.assignedToName.split(' ')[0]}
                            </span>
                          </div>
                        )}
                      </div>
                      
                      {/* Comments indicator (placeholder) */}
                      <div className="flex items-center gap-3 text-xs text-gray-500 dark:text-gray-400">
                        <div className="flex items-center gap-1">
                          <ChatBubbleLeftIcon className="h-3.5 w-3.5" />
                          <span>0</span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Add Task Button */}
            <button 
              onClick={() => setCreatingTaskStatus(column.status)}
              className="w-full mt-3 py-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors flex items-center justify-center gap-2"
            >
              <PlusIcon className="h-4 w-4" />
              <span>Új feladat</span>
            </button>
          </div>
        ))}
      </div>

      {/* Task Detail Modal */}
      {selectedTask && !editingTask && (
        <TaskView
          task={selectedTask}
          isOpen={!!selectedTask}
          onClose={() => setSelectedTask(null)}
          onEdit={(task) => setEditingTask(task)}
        />
      )}

      {/* Task Edit Form */}
      {editingTask && (
        <TaskForm
          initialData={editingTask}
          isOpen={!!editingTask}
          onClose={() => {
            setEditingTask(null);
            setSelectedTask(null);
          }}
          onSubmit={async (taskData) => {
            try {
              await updateTask(editingTask.id, taskData);
              setEditingTask(null);
              setSelectedTask(null);
            } catch (error) {
              console.error('Error updating task:', error);
            }
          }}
        />
      )}

      {/* Create Task Form */}
      {creatingTaskStatus && (
        <TaskForm
          isOpen={!!creatingTaskStatus}
          preSelectedProjectId={project.id}
          onClose={() => setCreatingTaskStatus(null)}
          onSubmit={async (taskData) => {
            try {
              // A TaskForm onSubmit-je már kezeli a létrehozást
              setCreatingTaskStatus(null);
            } catch (error) {
              console.error('Error creating task:', error);
            }
          }}
        />
      )}

      {/* Delete Confirmation Dialog */}
      {deletingTask && (
        <ConfirmDialog
          isOpen={!!deletingTask}
          onClose={() => setDeletingTask(null)}
          onConfirm={async () => {
            try {
              await deleteTask(deletingTask.id);
              setDeletingTask(null);
            } catch (error) {
              console.error('Error deleting task:', error);
            }
          }}
          title="Feladat törlése"
          message={`Biztosan törölni szeretnéd ezt a feladatot?`}
          itemName={deletingTask.title}
          confirmText="Törlés"
          cancelText="Mégse"
          type="danger"
        />
      )}
    </div>
  );
};

export default ProjectBoardView;
